import React, {Ref, useEffect, useState} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

interface Props
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  label?: string | React.ReactNode;
  subLabel?: string | React.ReactNode;
  checked?: boolean;
  dataTestId?: string;
  fullLabelWidth?: boolean;
}

export const Checkbox = ({
  label,
  subLabel,
  checked: propsChecked,
  className,
  dataTestId,
  fullLabelWidth,
  ref,
  ...props
}: Props & {ref?: Ref<HTMLInputElement>}) => {
  const [checked, setChecked] = useState(propsChecked);

  useEffect(() => {
    if (checked !== propsChecked) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setChecked(propsChecked);
    }
  }, [propsChecked]);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(e);
    setChecked(e.target.checked);
  };

  return (
    <Wrapper
      as={label ? 'label' : 'div'}
      data-testid={dataTestId ?? (label ? 'checkboxLabel' : 'checkboxWrapper')}
      className={className}
      data-full-width={fullLabelWidth}
    >
      <InputWrapper>
        <CheckboxInput
          {...props}
          checked={checked}
          data-testid="checkboxInput"
          onChange={e => handleToggle(e)}
          ref={ref}
          type="checkbox"
        />
        <CheckboxUI data-testid="checkboxCheckmark">
          <CheckmarkIcon
            asset={GenericIconName.Checkmark}
            data-testid="checkboxCheckmarkIcon"
            size="XS"
          />
        </CheckboxUI>
        {label}
      </InputWrapper>
      {subLabel}
    </Wrapper>
  );
};

const Wrapper = styled.label`
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  cursor: pointer;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.43;
  color: ${COLORS.Grey1};
`;

export const InputWrapper = styled.div`
  display: flex;
`;

const CheckboxUI = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 2px;
  border: 2px solid ${COLORS['Fill.Grey.Secondary']};
  box-sizing: border-box;
  transition: border-color 100ms, background-color 200ms;
  margin-right: 15px;
`;

const CheckmarkIcon = styled(Icon)`
  transform: scale(0.5);
  fill: ${COLORS.White};
  opacity: 0;
  transition: transform 300ms, opacity 100ms;
`;

const CheckboxInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;

  &:checked {
    color: ${COLORS.Black};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    & ~ ${CheckboxUI} {
      border-color: ${COLORS.Black};
      background-color: ${COLORS.Black};

      ${CheckmarkIcon} {
        opacity: 1;
        transform: scale(1.3);
      }
    }
  }

  &:focus-visible {
    & ~ ${CheckboxUI} {
      border-color: ${COLORS.Black};
    }
  }
`;
